import { Modal } from '@mantine/core';
import { IconBrandDocker, IconKey, IconRefresh } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Button, InputField } from '../../../../../components/Atoms';
import { ApplicationState } from '../../../../../store';

const REGISTRY_HOST = 'artifact.xver.cloud';
const MASKED_PASSWORD = '••••••••••••';

interface AuthenticationModalProps {
    opened: boolean;
    onClose: () => void;
    onResetRequested: () => void;
}

export default function AuthenticationModal({
    opened,
    onClose,
    onResetRequested,
}: AuthenticationModalProps) {
    const { t } = useTranslation('artifactRegistry');
    const user = useSelector((state: ApplicationState) => state.auth.user);
    const email = user?.email ?? '';

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            centered
            size="sm"
            title={
                <div className="inline-flex items-center gap-2">
                    <IconKey size={20} />
                    <span className="font-semibold">
                        {t('auth.modal.title', 'Registry authentication')}
                    </span>
                </div>
            }
        >
            <div className="w-full flex flex-col gap-4">
                <div className="w-full flex flex-row items-center gap-2 p-3 rounded-sm bg-surface-1 border border-line text-sm text-fg/78">
                    <IconBrandDocker size={20} stroke={1.5} />
                    <span className="font-mono">docker login {REGISTRY_HOST}</span>
                </div>
                <span className="text-sm text-fg/78">
                    {t('auth.modal.description', 'Use the following credentials to authenticate:')}
                </span>

                <InputField
                    readOnly
                    size="md"
                    label={t('auth.modal.username', 'Username')}
                    value={email}
                    placeholder={email}
                    showCopy
                />

                <p className="text-xs text-fg/68">
                    {t(
                        'auth.modal.helper',
                        "Your password is shown only once at creation. If you've lost it, generate a new one."
                    )}
                </p>

                <div className="w-full flex flex-row justify-end items-center gap-2 mt-1">
                    <Button variant="secondary" onClick={onClose}>
                        {t('auth.modal.close', 'Close')}
                    </Button>
                    <Button
                        variant="danger"
                        leftIcon={<IconRefresh size={16} stroke={1.5} />}
                        onClick={onResetRequested}
                    >
                        {t('auth.modal.reset', 'Reset password')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
